import { action, observable } from "mobx";
import { TimeSeriesDataPoint, TimeSeriesDataStore } from "./time-series-data.interface";

/**
 * MobX store for time series data
 */
class TimeSeriesDataStoreImpl implements TimeSeriesDataStore {
    @observable data: TimeSeriesDataPoint[] = [];

    constructor(initialData: TimeSeriesDataPoint[] = []) {
        // In MobX 4, properties are automatically made observable when decorated
        this.data = initialData;
    }

    /**
     * Add a new data point to the store
     * @param dataPoint The data point to add
     */
    @action
    addDataPoint(dataPoint: TimeSeriesDataPoint): void {
        // Create a new array instead of mutating the existing one
        this.data = [...this.data, dataPoint];
    }

    /**
     * Update a data point in the store
     * @param index The index of the data point to update
     * @param dataPoint The new data point
     */
    @action
    updateDataPoint(index: number, dataPoint: TimeSeriesDataPoint): void {
        if (index >= 0 && index < this.data.length) {
            // Create a new array with the updated item
            const newData = [...this.data];
            newData[index] = dataPoint;
            this.data = newData;
        }
    }

    /**
     * Remove a data point from the store
     * @param index The index of the data point to remove
     */
    @action
    removeDataPoint(index: number): void {
        if (index >= 0 && index < this.data.length) {
            // Create a new array without the removed item
            this.data = this.data.filter((_, i) => i !== index);
        }
    }

    /**
     * Clear all data points from the store
     */
    @action
    clearData(): void {
        this.data = [];
    }

    /**
     * Set all data points in the store
     * @param data The data points to set
     */
    @action
    setData(data: TimeSeriesDataPoint[]): void {
        this.data = data;
    }
}

/**
 * Create a new time series data store
 * @param initialData Initial data points
 * @returns A new time series data store
 */
export const createTimeSeriesDataStore = (initialData: TimeSeriesDataPoint[] = []): TimeSeriesDataStore => {
    return new TimeSeriesDataStoreImpl(initialData);
};
